import type {
  GeoJSONData,
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  GeoJSONGeometry,
  GeoJSONType,
} from "./types";

/**
 * Extracts unique property keys from GeoJSON features
 */
export const extractPropertyKeys = (features: GeoJSONFeature[]): string[] => {
  const keySet = new Set<string>();
  features.forEach((feature) => {
    if (feature.properties) {
      Object.keys(feature.properties).forEach((key) => keySet.add(key));
    }
  });
  return Array.from(keySet).sort();
};

/**
 * Generates a timestamp in the format YYYYMMDD_HHMM for name collision resolution
 */
export const generateTimestamp = (): string => {
  return new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[-:T]/g, "")
    .replace(/(\d{8})(\d{4})/, "$1_$2");
};

/**
 * Resolves name collisions by appending timestamps to duplicate names
 */
export const resolveNameCollisions = (
  features: GeoJSONFeature[],
  selectedNameKey: string,
  timestamp?: string
): string[] => {
  const nameCount = new Map<string, number>();
  const resolvedNames: string[] = [];
  const timestampToUse = timestamp || generateTimestamp();

  features.forEach((feature) => {
    const baseName = (feature.properties?.[selectedNameKey] as string) || "";
    const baseNameStr = String(baseName);

    const currentCount = nameCount.get(baseNameStr) || 0;
    nameCount.set(baseNameStr, currentCount + 1);

    const finalName =
      currentCount > 0 ? `${baseNameStr}_${timestampToUse}` : baseNameStr;
    resolvedNames.push(finalName);
  });

  return resolvedNames;
};

/**
 * Transforms different GeoJSON types into a normalized feature array
 */
export const transformToFeatures = (
  geoJsonData: GeoJSONData,
  geoJsonType: GeoJSONType
): GeoJSONFeature[] => {
  if (geoJsonType === "FeatureCollection") {
    const featureCollection = geoJsonData as GeoJSONFeatureCollection;
    return featureCollection.features;
  } else if (geoJsonType === "Feature") {
    const feature = geoJsonData as GeoJSONFeature;
    return [feature];
  } else if (geoJsonType === "Geometry") {
    const geometry = geoJsonData as GeoJSONGeometry;
    return [
      {
        type: "Feature",
        properties: { name: "Boundary" },
        geometry: {
          type: geometry.type,
          coordinates: geometry.coordinates,
        },
      },
    ];
  }
  return [];
};

/**
 * Validates if a file has the correct GeoJSON extension
 */
export const isValidGeoJSONFile = (fileName: string): boolean => {
  return fileName.toLowerCase().endsWith(".geojson");
};

/**
 * Generates feature names based on GeoJSON type and selected property key
 */
export const generateFeatureNames = (
  features: GeoJSONFeature[],
  geoJsonType: GeoJSONType,
  selectedNameKey?: string,
  boundaryName?: string
): string[] => {
  if (features.length === 0) return [];

  if (geoJsonType === "FeatureCollection") {
    if (!selectedNameKey) return [];
    return features.map((feature, index) => {
      const name = feature.properties?.[selectedNameKey];
      return name ? String(name) : `Feature ${index + 1}`;
    });
  } else if (geoJsonType === "Feature") {
    const feature = features[0];
    return [boundaryName || (feature?.properties?.name as string) || "Feature"];
  } else if (geoJsonType === "Geometry") {
    return [boundaryName || "Boundary"];
  }

  return [];
};

/**
 * Processes features for import with name collision resolution
 */
export const processFeatureCollectionForImport = (
  features: GeoJSONFeature[],
  selectedNameKey: string
): GeoJSONFeature[] => {
  const nameCount = new Map<string, number>();
  const timestamp = generateTimestamp();

  return features.map((feature) => {
    const baseName =
      feature.properties?.[selectedNameKey] ||
      `Feature ${feature.properties?.id || "Unknown"}`;
    const baseNameStr = String(baseName);

    const currentCount = nameCount.get(baseNameStr) || 0;
    nameCount.set(baseNameStr, currentCount + 1);

    const finalName =
      currentCount > 0 ? `${baseNameStr}_${timestamp}` : baseNameStr;

    return {
      ...feature,
      properties: {
        ...feature.properties,
        name: finalName,
      },
    };
  });
};

/**
 * Processes a single feature for edit with name collision awareness
 */
export const processFeatureForEdit = (
  features: GeoJSONFeature[],
  selectedFeatureIndex: number,
  selectedNameKey: string
): { featureName: string; hasCollision: boolean } => {
  const selectedFeature = features[selectedFeatureIndex];
  if (!selectedFeature) {
    return {
      featureName: `Feature ${selectedFeatureIndex + 1}`,
      hasCollision: false,
    };
  }

  const baseName =
    (selectedFeature.properties?.[selectedNameKey] as string) ||
    `Feature ${selectedFeatureIndex + 1}`;

  // Check for name collisions in the feature collection
  const nameCount = new Map<string, number>();
  features.forEach((feature) => {
    const name = (feature.properties?.[selectedNameKey] as string) || "";
    const currentCount = nameCount.get(name) || 0;
    nameCount.set(name, currentCount + 1);
  });

  const baseNameCount = nameCount.get(baseName) || 0;
  const hasCollision = baseNameCount > 1;

  if (hasCollision) {
    // Only add timestamp if there are duplicates and this isn't the first occurrence
    let duplicateIndex = 0;
    for (let i = 0; i < selectedFeatureIndex; i++) {
      const otherName =
        (features[i]?.properties?.[selectedNameKey] as string) || "";
      if (otherName === baseName) {
        duplicateIndex++;
      }
    }

    if (duplicateIndex > 0) {
      const timestamp = generateTimestamp();
      return { featureName: `${baseName}_${timestamp}`, hasCollision: true };
    }
  }

  return { featureName: baseName, hasCollision };
};
